import React from "react";
import {
  Box,
  Button,
  Backdrop,
  Dialog,
  Divider,
  IconButton,
  makeStyles,
  Typography,
  FormHelperText,
  CircularProgress,
} from "@material-ui/core";
import { ErrorMessage, Formik, setNestedObjectValues } from "formik";
import { getLang } from "app/feature/constants";
import { Close as CloseIcon } from "@material-ui/icons";
import InputTextField from "components/input/inputTextField";
import { convertToBigNum } from "lib/generalUtility";
import * as Yup from "yup";
import LoyaltyPointProductSelect from "../../select/loyaltyPointProductSelect";
import { ModuleTable } from "../../table";
import InstantWinSelect from "../../select/instantWinSelect";
import moment from "moment";
import InstantWinPrizeTable from "../../table/instantWinPrizeTable";
import { Skeleton } from "@material-ui/lab";
import RefreshTable from "components/table/refreshTable";

const useStyles = makeStyles(() => ({
  dialog: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  field: {
    display: "grid",
    gridTemplateColumns: "1fr 3fr",
    gap: "16px",
  },
  instantWinBox: {
    border: "1px solid #E5E5E5",
    borderRadius: 4,
  },
  dateField: {
    backgroundColor: "#F9FAFB",
    borderRadius: 4,
    display: "grid",
    gridTemplateColumns: "1fr 3fr",
    "& >div:first-child": {
      borderRight: "1px solid #0000001a",
      textAlign: "center",
      color: "#3a4d5454",
      display: "flex",
      justifyContent: "center",
    },
    "& div": {
      padding: 8,
      display: "flex",
      alignItems: "center",
      fontWeight: 700,
    },
  },
}));

export default function AddPointDialogComponent({
  isOpen,
  handleClose,
  hasLoyaltyProgramEditAccessRight,
  detail,
  handleSubmit,
  lang,
  type,
  isSubmitting,
  language,
  isLoading,
  handleReload,
  isError,
}) {
  const classes = useStyles();

  if (language === "ZH") {
    moment.locale("zh-cn");
  } else if (language === "TL") {
    moment.locale("tl-ph");
  } else if (language === "EN") {
    moment.defineLocale("en-custom", {
      parentLocale: "en",
      longDateFormat: {
        ll: "DD MMM YYYY", // customize this line
      },
    });
    moment.locale("en-custom");
  } else {
    moment.locale(language);
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      disableEnforceFocus
      disableAutoFocus
      disableEscapeKeyDown
      disableBackdropClick
      closeAfterTransition
      BackdropComponent={Backdrop}
      maxWidth="md"
      classes={{
        paperWidthMd: classes.dialog,
      }}
    >
      <Box>
        <Formik
          enableReinitialize
          initialValues={{
            point: detail?.amount || "",
            products: detail?.products ?? [],
            instantWins: detail?.instant_win ?? [],
            isEdit: false,
          }}
          validationSchema={Yup.object({
            point: Yup.string().required(
              getLang(lang, "message.error.FORM_VALIDATE_REQUIRED")
            ),
            products: Yup.string().required(
              getLang(lang, "message.error.FORM_VALIDATE_REQUIRED")
            ),
          })}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit}>
              <Box
                px={2}
                py={1.5}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  variant="subtitle2"
                  color="primary"
                  className={classes.headerText}
                >
                  {type === "create"
                    ? getLang(lang, "label.ADD_LOWERCASE")
                    : formik.values.isEdit
                    ? getLang(lang, "label.EDIT")
                    : getLang(lang, "label.VIEW")}{" "}
                  {getLang(lang, "label.POINT_LOWERCASE")}
                </Typography>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleClose}
                  className={classes.iconButton}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Divider />
              <Box p={2} maxHeight="70dvh" overflow="auto">
                {isError ? (
                  <RefreshTable handleReload={handleReload} />
                ) : isLoading ? (
                  <>
                    <Skeleton variant="text" height={30} />
                    <Skeleton variant="text" height={30} />
                    <Skeleton variant="text" height={30} />
                    <Skeleton variant="text" height={30} />
                  </>
                ) : (
                  <>
                    <Box className={classes.field}>
                      <Box
                        style={{
                          marginTop:
                            type === "view" && !formik.values.isEdit ? 0 : 10,
                        }}
                      >
                        <Typography variant="body2">
                          {getLang(lang, "label.POINT")}{" "}
                          <span style={{ color: "red" }}>*</span>
                        </Typography>
                      </Box>
                      <Box>
                        {type === "view" && !formik.values.isEdit ? (
                          <Typography
                            variant="body2"
                            color="primary"
                            className={classes.loyaltyPointText}
                          >
                            {convertToBigNum(formik.values.point)}
                          </Typography>
                        ) : (
                          <InputTextField
                            disabled={isSubmitting}
                            required
                            variant="filled"
                            size="small"
                            style={{
                              width: "100%",
                            }}
                            fullWidth
                            type="number"
                            inputProps={{
                              min: 1,
                            }}
                            placeholder={getLang(
                              lang,
                              "placeholder.ENTER_POINT_HERE"
                            )}
                            {...formik.getFieldProps("point")}
                            error={
                              formik.touched.point && formik.errors.point
                                ? true
                                : false
                            }
                            helperText={ErrorMessage({
                              name: "point",
                            })}
                          />
                        )}
                      </Box>
                    </Box>
                    <Box className={classes.field} mt={2}>
                      <Box style={{ marginTop: 10 }}>
                        <Typography variant="body2">
                          {getLang(lang, "label.ASSIGN_PRODUCT")}{" "}
                          <span style={{ color: "red" }}>*</span>
                        </Typography>
                      </Box>
                      <Box>
                        {(type === "create" || formik.values.isEdit) && (
                          <LoyaltyPointProductSelect
                            disabled={isSubmitting}
                            value={formik.values.products}
                            required
                            handleChange={(value) => {
                              formik.setFieldValue("products", value);
                              formik.setFieldValue("instantWins", []);
                            }}
                          />
                        )}
                        {formik.touched.products && formik.errors.products && (
                          <FormHelperText
                            style={{ margin: "4px 14px 0px" }}
                            error={
                              formik.touched.products && formik.errors.products
                                ? true
                                : false
                            }
                          >
                            {ErrorMessage({ name: "products" })}
                          </FormHelperText>
                        )}
                        <ModuleTable tableData={formik.values.products} />
                      </Box>
                    </Box>
                    <Box className={classes.field} mt={2}>
                      <Box style={{ marginTop: 10 }}>
                        <Typography variant="body2">
                          {getLang(lang, "label.INSTANT_WIN_BINDING")}
                        </Typography>
                      </Box>
                      <Box>
                        {(type === "create" || formik.values.isEdit) && (
                          <InstantWinSelect
                            disabled={
                              isSubmitting || !formik.values.products.length
                            }
                            value={formik.values.instantWins}
                            handleChange={(value) => {
                              formik.setFieldValue("instantWins", value);
                            }}
                            productUuids={formik.values.products.map(
                              (p) => p.id
                            )}
                            id={detail?.id}
                          />
                        )}
                        {formik.values.instantWins.length > 0 ? (
                          <Box className={classes.instantWinBox}>
                            {formik.values.instantWins.map(
                              (instantWin, index) => (
                                <Box p={2} key={index}>
                                  {index > 0 && (
                                    <Box my={2}>
                                      <Divider />
                                    </Box>
                                  )}
                                  <Box className={classes.field}>
                                    <Typography variant="body2">
                                      {getLang(lang, "label.INSTANT_WIN_NAME")}
                                    </Typography>
                                    <Typography variant="body2">
                                      <b>{instantWin.name}</b>
                                    </Typography>
                                  </Box>
                                  <Box className={classes.field} mt={2}>
                                    <Typography variant="body2">
                                      {getLang(lang, "label.CAMPAIGN_PERIOD")}
                                    </Typography>
                                    <Box>
                                      <Box className={classes.dateField}>
                                        <Box>
                                          {getLang(lang, "label.START")}
                                        </Box>
                                        <Box>
                                          {moment(instantWin.start_at).format(
                                            "ll"
                                          )}
                                        </Box>
                                      </Box>
                                      <Box className={classes.dateField} mt={2}>
                                        <Box>{getLang(lang, "label.END")}</Box>
                                        <Box>
                                          {moment(instantWin.end_at).format(
                                            "ll"
                                          )}
                                        </Box>
                                      </Box>
                                    </Box>
                                  </Box>
                                  <Box mt={2}>
                                    <InstantWinPrizeTable
                                      tableData={instantWin.prizes ?? []}
                                    />
                                  </Box>
                                </Box>
                              )
                            )}
                          </Box>
                        ) : (
                          type !== "create" &&
                          !formik.values.isEdit && (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              style={{ marginTop: 10 }}
                            >
                              -
                            </Typography>
                          )
                        )}
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
              {!isError &&
                ((type === "view" && hasLoyaltyProgramEditAccessRight) ||
                  type === "create") && (
                  <>
                    <Divider />

                    <Box
                      px={2}
                      py={1}
                      display="flex"
                      justifyContent="space-between"
                    >
                      {formik.values.isEdit ? (
                        <Button
                          variant="text"
                          onClick={() => {
                            type === "create"
                              ? handleClose()
                              : formik.resetForm();
                          }}
                          disabled={isSubmitting}
                        >
                          {getLang(lang, "label.CANCEL")}
                        </Button>
                      ) : (
                        <Box />
                      )}

                      <Button
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                          if (type === "create" || formik.values.isEdit) {
                            const errors = await formik.validateForm();

                            if (Object.keys(errors).length === 0) {
                              formik.handleSubmit();
                            } else {
                              formik.setTouched(
                                setNestedObjectValues(errors, true)
                              );
                            }
                          } else {
                            formik.setFieldValue("isEdit", true);
                          }
                        }}
                        startIcon={
                          isSubmitting && (
                            <CircularProgress
                              style={{ width: 15, height: 15, color: "white" }}
                            />
                          )
                        }
                        disabled={isSubmitting}
                      >
                        {type === "create"
                          ? getLang(lang, "label.ADD")
                          : formik.values.isEdit
                          ? getLang(lang, "label.SAVE")
                          : getLang(lang, "label.EDIT")}
                      </Button>
                    </Box>
                  </>
                )}
            </form>
          )}
        </Formik>
      </Box>
    </Dialog>
  );
}
